import * as React from 'react';
import { useLocale, type Locale } from '@ministry/ui/internal';
import Globe from 'lucide-react/dist/esm/icons/globe.js';

import { useT } from '../i18n/strings.js';

const LOCALES: Locale[] = ['en', 'ne'];

/**
 * The language toggle, as its own component so that it can appear on the screens that
 * render OUTSIDE `AppShell` — login and MFA.
 *
 * Compact icon control (a globe + the current locale's 2-letter code), not a full
 * text button reading "Switch language: NE" (~130px, previously) — ux-review defect:
 * two verbose text buttons (this and the theme toggle) dominated the top bar. The
 * visible "NE"/"EN" text is decorative-but-present (WCAG 2.5.3 Label in Name: it is
 * included inside the fuller `aria-label` below, not replaced by it), and the
 * accessible name stays a complete sentence naming both the current language and what
 * the control does, never just an icon with no name.
 */
export function LocaleToggle(): React.JSX.Element {
  const { locale, setLocale } = useLocale();
  const t = useT();
  const next = (): Locale => LOCALES[(LOCALES.indexOf(locale) + 1) % LOCALES.length] as Locale;
  const nextLocale = next();

  const currentCode = locale === 'ne' ? 'NE' : 'EN';
  const accessibleName = t('shell.locale.switchTo', {
    current: locale === 'ne' ? t('shell.locale.nepali') : t('shell.locale.english'),
    next: nextLocale === 'ne' ? t('shell.locale.nepali') : t('shell.locale.english'),
  });

  return (
    <button
      type="button"
      onClick={() => setLocale(nextLocale)}
      aria-label={accessibleName}
      title={accessibleName}
      className="inline-flex min-h-touch min-w-touch items-center justify-center gap-1 rounded-md px-2 text-sm font-semibold text-text-primary transition-colors duration-fast hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-focus"
    >
      <Globe aria-hidden="true" width={18} height={18} />
      <span aria-hidden="true">{currentCode}</span>
    </button>
  );
}
